import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAdminComplexById, fetchSipClientAssignments, fetchSipClientOptions, resolveSipClients, SIP_CLIENT_DIRECTORY_CHANGED_EVENT } from "../../../admin/api";
import EditComplexModal from "../EditComplexModal";
import { adminStyles as styles } from "../shared/adminStyles";
import { useAdminTextSearch } from "../utils/useAdminTextSearch";
import WorkspaceSelectionToggle from "../workspaceSelection/WorkspaceSelectionToggle";
import EditComplexButton from "../EditComplexButton";
import { createSipClientSelectionItem, getWorkspaceSelectionKey } from "../workspaceSelection/workspaceSelectionUtils";
import TemporarySelectionBar from "../workspaceSelection/TemporarySelectionBar";
import TemporarySelectionCheckbox from "../workspaceSelection/TemporarySelectionCheckbox";
import useTemporarySelection from "../workspaceSelection/useTemporarySelection";
import BulkSipStatusActions from "../bulkSipActions/BulkSipStatusActions";
import BulkSipTableEditorAction from "../bulkSipActions/BulkSipTableEditorAction";
import BulkSipApplyAction from "../bulkSipActions/BulkSipApplyAction";
import { useSessionSipActivity } from "../sessionActivity/SessionSipActivityContext";
import SessionChangedBadge from "../sessionActivity/SessionChangedBadge";
import SessionActivityControls from "../sessionActivity/SessionActivityControls";
import SavedViewsControls from "../savedViews/SavedViewsControls";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import AdminStatusBadge from "../shared/AdminStatusBadge";
import { htmlToPlainText } from "../../../utils/htmlToPlainText";
import { useWorkspaceSelection } from "../workspaceSelection/WorkspaceSelectionContext";
import { useWorkspaceSelectionMode } from "../workspaceSelection/WorkspaceSelectionModeContext";
import WorkspaceSelectionModeBar from "../workspaceSelection/WorkspaceSelectionModeBar";
import "./SipDispatcherSection.css";
const DEFAULT_SIP_DISPATCHER_VIEW = {
  search: "",
  filters: { onlySessionChanged: false, launchStatus: "all", clientStatus: "all", complexStatus: "all" },
  sort: { field: "complexName", direction: "asc" },
  pageSize: null,
  settings: { selectedClientId: "" },
};
export const SIP_ASSIGNMENTS_COLUMN_WIDTHS = [4, 20, 10, 23, 12, 8, 8, 7, 8];
export default function SipDispatcherSection() {
  const [clientOptions, setClientOptions] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [editingComplex, setEditingComplex] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [onlySessionChanged, setOnlySessionChanged] = useState(false);
  const [clientStatus, setClientStatus] = useState("all");
  const [launchStatus, setLaunchStatus] = useState("all");
  const [complexStatus, setComplexStatus] = useState("all");
  const [clientSummary, setClientSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [assignmentsVersion, setAssignmentsVersion] = useState(0);
  const [workspaceResolvedCount, setWorkspaceResolvedCount] = useState(0);
  const loadedClientIdRef = useRef("");
  const restoredWorkspaceViewRef = useRef(false);
  const { isChanged } = useSessionSipActivity();
  const { invalidate, versions } = useAdminDataRefresh();
  const debouncedQuery = useAdminTextSearch(query);
  const { items: workspaceItems } = useWorkspaceSelection();
  const { activeGroup, activeView, restoreState, exitMode, registerSectionState } = useWorkspaceSelectionMode();
  const isWorkspaceMode = activeGroup === "sipClients";
  const workspaceSipItems = useMemo(() => workspaceItems.filter((item) => item.entityType === "sipClient"), [workspaceItems]);
  const workspaceSipKeys = useMemo(() => new Set(workspaceSipItems.map(getWorkspaceSelectionKey)), [workspaceSipItems]);

  useEffect(() => {
    let active = true;

    async function loadComplexes() {
      try {
        setIsLoading(true);
        setError("");

        const data = await fetchSipClientOptions();

        if (!active) return;

        setClientOptions(Array.isArray(data?.items) ? data.items : []);
      } catch (loadError) {
        if (!active) return;

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Не удалось загрузить клиентов"
        );
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadComplexes();
    window.addEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadComplexes);

    return () => {
      active = false;
      window.removeEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadComplexes);
    };
  }, []);

  useEffect(() => {
    if (selectedClientId && !clientOptions.some((client) => String(client?._id || "") === selectedClientId)) setSelectedClientId("");
  }, [clientOptions, selectedClientId]);

  useEffect(() => {
    if (isWorkspaceMode) {
      if (workspaceSipItems.length === 0) {
        setClientRows([]);
        setWorkspaceResolvedCount(0);
        setIsLoading(false);
        setError("");
        return undefined;
      }
      const controller = new AbortController();
      setIsLoading(true);
      setError("");
      resolveSipClients({ items: workspaceSipItems.map((item) => ({ complexId: item.complexId, sipClientId: item.sipClientId })) }, { signal: controller.signal })
        .then((data) => {
          const rows = Array.isArray(data?.results) ? data.results.filter((item) => item.ok) : [];
          setClientRows(rows);
          setWorkspaceResolvedCount(rows.length);
          setClientSummary({
            total: rows.length,
            active: rows.filter((row) => row.clientStatus).length,
            inactive: rows.filter((row) => !row.clientStatus).length,
          });
        })
        .catch((loadError) => {
          if (loadError?.name !== "AbortError") setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить рабочую подборку");
        })
        .finally(() => { if (!controller.signal.aborted) setIsLoading(false); });
      return () => controller.abort();
    }
    if (loadedClientIdRef.current !== selectedClientId) {
      setClientRows([]);
      setClientSummary({ total: 0, active: 0, inactive: 0 });
    }
    loadedClientIdRef.current = selectedClientId;
    if (!selectedClientId) {
      setIsLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError("");
    fetchSipClientAssignments(selectedClientId, {
      clientStatus,
      launchStatus,
      complexStatus,
      query: debouncedQuery.value,
    }, { signal: controller.signal })
      .then((data) => {
        setClientRows(Array.isArray(data?.items) ? data.items : []);
        setClientSummary({
          total: Number(data?.summary?.total) || 0,
          active: Number(data?.summary?.active) || 0,
          inactive: Number(data?.summary?.inactive) || 0,
        });
      })
      .catch((loadError) => {
        if (loadError?.name !== "AbortError") setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить назначения клиента");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [assignmentsVersion, clientStatus, complexStatus, debouncedQuery.value, isWorkspaceMode, launchStatus, selectedClientId, versions.sip, workspaceSipItems]);

  const selectedClient = useMemo(
    () => clientOptions.find((client) => String(client?._id || "") === selectedClientId) || null,
    [clientOptions, selectedClientId]
  );
  const filteredRows = useMemo(() => {
    return clientRows.filter((row) => {
      if (isWorkspaceMode && !workspaceSipKeys.has(getWorkspaceSelectionKey(createSipClientSelectionItem(row)))) return false;
      if (isWorkspaceMode && selectedClientId && String(row.clientId || "") !== selectedClientId) return false;
      if (isWorkspaceMode && clientStatus !== "all" && Boolean(row.clientStatus) !== (clientStatus === "active")) return false;
      if (isWorkspaceMode && launchStatus !== "all") {
        const launched = Boolean(row.complexStatus) && String(row.currentSipClientId || "") === String(row.sipClientId || "");
        if (launched !== (launchStatus === "launched")) return false;
      }
      if (isWorkspaceMode && complexStatus !== "all" && Boolean(row.complexStatus) !== (complexStatus === "active")) return false;
      if (isWorkspaceMode && debouncedQuery.value) {
        const searchText = [row.complexName, row.sip_number, row.transfer_number, row.clientName].join(" ").toLocaleLowerCase("ru-RU");
        if (!searchText.includes(debouncedQuery.value.toLocaleLowerCase("ru-RU"))) return false;
      }
      if (onlySessionChanged && !isChanged(createSipClientSelectionItem(row))) return false;
      return true;
    });
  }, [clientRows, clientStatus, complexStatus, debouncedQuery.value, isChanged, isWorkspaceMode, launchStatus, onlySessionChanged, selectedClientId, workspaceSipKeys]);

  const visibleSelectionItems = useMemo(
    () => filteredRows.map(createSipClientSelectionItem),
    [filteredRows]
  );
  const temporarySelection = useTemporarySelection(visibleSelectionItems);
  const { replaceSelection: replaceTemporarySelection } = temporarySelection;
  const currentSavedView = useMemo(() => ({
    search: query,
    filters: { onlySessionChanged, launchStatus, clientStatus, complexStatus },
    sort: { field: "complexName", direction: "asc" },
    pageSize: null,
    settings: { selectedClientId },
  }), [clientStatus, complexStatus, launchStatus, onlySessionChanged, query, selectedClientId]);
  const applySavedView = useCallback((view) => {
    setQuery(view.search || "");
    setOnlySessionChanged(Boolean(view.filters?.onlySessionChanged));
    setLaunchStatus(["launched", "not_launched"].includes(view.filters?.launchStatus) ? view.filters.launchStatus : "all");
    setClientStatus(["active", "stopped", "inactive"].includes(view.filters?.clientStatus)
      ? (view.filters.clientStatus === "inactive" ? "stopped" : view.filters.clientStatus)
      : "all");
    setComplexStatus(["active", "stopped"].includes(view.filters?.complexStatus) ? view.filters.complexStatus : "all");
    const savedClientId = view.settings?.selectedClientId || "";
    setSelectedClientId(clientOptions.some((client) => String(client?._id || "") === savedClientId) ? savedClientId : "");
  }, [clientOptions]);

  const handleSaved = (savedComplex, options = {}) => {
    if (!savedComplex?._id) {
      if (options.close !== false) setEditingComplex(null);
      return;
    }
    if (options.close === false) setEditingComplex(savedComplex);
    else setEditingComplex(null);
    invalidate(["sip"]);
  };

  const handleStatusApplied = (results) => {
    if (results) setAssignmentsVersion((value) => value + 1);
  };

  const handleBulkEditApplied = (results) => {
    if (results) setAssignmentsVersion((value) => value + 1);
  };
  useEffect(() => {
    if (isWorkspaceMode && activeView && !restoredWorkspaceViewRef.current) {
      restoredWorkspaceViewRef.current = true;
      applySavedView(activeView);
    }
  }, [activeView, applySavedView, isWorkspaceMode]);
  useEffect(() => {
    registerSectionState("sipClients", currentSavedView);
  }, [currentSavedView, registerSectionState]);
  useEffect(() => {
    if (isWorkspaceMode) replaceTemporarySelection(visibleSelectionItems);
  }, [isWorkspaceMode, replaceTemporarySelection, visibleSelectionItems]);
  const exitWorkspaceMode = () => {
    if (restoreState) applySavedView(restoreState);
    exitMode();
  };

  const openComplex = async (complexId) => {
    try {
      setError("");
      setEditingComplex(await fetchAdminComplexById(complexId));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить ЖК");
    }
  };

  return (
    <>
      <section style={styles.card}>
        <div className="workspace-selection-search-header">
          <div className="workspace-selection-search-header__saved">
            <SavedViewsControls
              sectionId="sip-dispatcher"
              currentView={currentSavedView}
              defaultView={DEFAULT_SIP_DISPATCHER_VIEW}
              onApply={applySavedView}
            />
          </div>
          {isWorkspaceMode && (
            <WorkspaceSelectionModeBar
              count={filteredRows.length}
              missingCount={Math.max(0, workspaceSipItems.length - workspaceResolvedCount)}
              onExit={exitWorkspaceMode}
            />
          )}
        </div>
        <div style={{ ...styles.filterActionsBar, justifyContent: "flex-end" }}>
          <p style={styles.resultCount}>
            Найдено: {filteredRows.length}, клиентов: {clientOptions.length}
          </p>
        </div>

        <div style={styles.controls}>
          <select
            style={styles.input}
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
          >
            <option value="">Выберите клиента</option>
            {clientOptions.length === 0 ? <option value="">Нет клиентов</option> : null}
            {clientOptions.map((client) => (
              <option key={client._id} value={client._id}>
                {client.name}
              </option>
            ))}
          </select>

          <input
            placeholder="Поиск по ЖК, СИПу или номеру перевода..."
            style={styles.input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "var(--admin-gap)",
            marginTop: "var(--admin-gap-lg)",
          }}
        >
          {[
            ["Всего назначений", clientSummary.total],
            ["Активный клиент", clientSummary.active],
            ["Неактивный клиент", clientSummary.inactive],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--admin-radius)",
                background: "var(--card-bg-soft)",
                padding: "var(--admin-card-padding)",
              }}
            >
              <div style={styles.muted}>{label}</div>
              <div style={{ ...styles.documentsTitle, marginTop: "var(--admin-gap-sm)" }}>{value}</div>
            </div>
          ))}
        </div>
        <div className="sip-dispatcher-filter-activity-row">
        <div className="sip-dispatcher-status-filters" aria-label="Быстрые фильтры клиентов">
          {[
            ["Запуск", launchStatus, setLaunchStatus, [["all", "Все"], ["launched", "Запущенные"], ["not_launched", "Не запущенные"]]],
            ["Клиент", clientStatus, setClientStatus, [["all", "Все"], ["active", "Активные"], ["stopped", "В стопе"]]],
            ["ЖК", complexStatus, setComplexStatus, [["all", "Все"], ["active", "Активные"], ["stopped", "В стопе"]]],
          ].map(([label, value, setter, options]) => (
            <label key={label} style={{ display: "grid", gap: "4px", minWidth: "150px", color: "var(--text-muted)", fontSize: "12px", fontWeight: 700 }}>
              {label}
              <select
                style={{ ...styles.input, minWidth: 0, padding: "7px 9px" }}
                value={value}
                onChange={(event) => setter(event.target.value)}
              >
                {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
              </select>
            </label>
          ))}
          <button
            type="button"
            style={styles.viewButton}
            disabled={launchStatus === "all" && clientStatus === "all" && complexStatus === "all"}
            onClick={() => {
              setLaunchStatus("all");
              setClientStatus("all");
              setComplexStatus("all");
            }}
          >
            Сбросить фильтры
          </button>
        </div>
        <SessionActivityControls
          className="session-activity-toolbar--sip-dispatcher"
          onlyChanged={onlySessionChanged}
          onOnlyChangedChange={setOnlySessionChanged}
        />
        </div>
      </section>

      <section style={styles.documentsCard}>
        <div style={styles.documentsHeader}>
          <h2 style={styles.documentsTitle}>
            {selectedClient ? `ЖК клиента: ${selectedClient.name}` : "ЖК клиента"}
          </h2>
          <span style={styles.muted}>{filteredRows.length} / {clientRows.length}</span>
        </div>

        <TemporarySelectionBar
          selectedItems={temporarySelection.selectedItems}
          onClear={temporarySelection.clearSelection}
          additionalActions={
            <>
              <BulkSipTableEditorAction
                items={temporarySelection.selectedItems}
                label="Редактировать таблицу клиентов"
                onApplied={handleBulkEditApplied}
                sourceSection="sip-dispatcher"
                buttonStyle={styles.viewButton}
              />
              <BulkSipStatusActions
                items={temporarySelection.selectedItems}
                onApplied={handleStatusApplied}
                sourceSection="sip-dispatcher"
                buttonStyle={styles.viewButton}
              />
              <BulkSipApplyAction
                items={temporarySelection.selectedItems}
                onApplied={handleStatusApplied}
                buttonStyle={styles.viewButton}
              />
            </>
          }
        />

        <div style={styles.documentsScroll}>
          {!isWorkspaceMode && !selectedClientId ? (
            <div style={styles.state}>Выберите клиента для загрузки данных</div>
          ) : isLoading ? (
            <div style={styles.state}>Загрузка клиентов...</div>
          ) : error ? (
            <div style={styles.state}>{error}</div>
          ) : clientOptions.length === 0 ? (
            <div style={styles.state}>В базе пока нет ЖК с заполненными клиентами</div>
          ) : filteredRows.length === 0 ? (
            <div style={styles.state}>
              {clientStatus === "active"
                ? "Нет ЖК с активным выбранным клиентом"
                : clientStatus === "stopped"
                  ? "Нет ЖК с неактивным выбранным клиентом"
                  : "Ничего не найдено"}
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={{ ...styles.table, tableLayout: "fixed" }}>
                <colgroup>
                  {SIP_ASSIGNMENTS_COLUMN_WIDTHS.map((width, index) => (
                    <col key={index} style={{ width: `${width}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th style={styles.th}>
                      <TemporarySelectionCheckbox
                        checked={temporarySelection.allVisibleSelected}
                        indeterminate={temporarySelection.partiallyVisibleSelected}
                        onChange={temporarySelection.toggleVisible}
                        label="Выбрать всех видимых клиентов"
                      />
                    </th>
                    <th style={styles.th}>ЖК</th>
                    <th style={styles.th}>Застройщик</th>
                    <th style={styles.th}>Тип перевода</th>
                    <th style={styles.th}>СИП</th>
                    <th style={styles.th}>Номер перевода</th>
                    <th style={styles.th}>Статус клиента</th>
                    <th style={styles.th}>Запущен</th>
                    <th style={styles.th}>Действия</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.map((row, index) => {
                    const typeText = htmlToPlainText(row.type);
                    const rowBackground =
                      index % 2 === 0 ? "var(--card-bg)" : "var(--card-bg-soft)";

                    return (
                      <tr
                        key={`${row.complexId}-${row.sipClientId}`}
                        style={{ background: rowBackground }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "var(--accent-soft)";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = rowBackground;
                        }}
                      >
                        <td style={styles.td}>
                          <TemporarySelectionCheckbox
                            checked={temporarySelection.isTemporarilySelected(
                              visibleSelectionItems[index]
                            )}
                            onChange={() => temporarySelection.toggleOne(visibleSelectionItems[index])}
                            label={`Выбрать клиента ${row.clientName || "Без имени"}`}
                          />
                        </td>
                        <td style={styles.td} title={row.complexName || ""}>
                          <div style={{ ...styles.name, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}>{row.complexName}</div>
                          <SessionChangedBadge item={visibleSelectionItems[index]} />
                        </td>
                        <td style={styles.td} title={row.developer || ""}>{row.developer || "—"}</td>
                        <td style={styles.td} title={typeText}>
                          <div className="admin-cell-ellipsis">{typeText || "—"}</div>
                        </td>
                        <td style={styles.td}>{row.sip_number || "—"}</td>
                        <td style={styles.td}>{row.transfer_number || "—"}</td>
                        <td style={styles.td}>
                          <AdminStatusBadge active={row.clientStatus} />
                        </td>
                        <td style={styles.td}>
                          {row.complexStatus === true && String(row.currentSipClientId || "") === String(row.sipClientId || "")
                            ? <span className="complex-edit-badge sip-launch-badge--active">Запущен</span>
                            : <span className="complex-edit-badge sip-client-badge--stopped">Не запущен</span>}
                        </td>
                        <td style={{ ...styles.td, ...styles.actionsTd }}>
                          <div className="admin-row-actions" style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                            <WorkspaceSelectionToggle item={createSipClientSelectionItem(row)} />
                            <EditComplexButton
                              label="Открыть ЖК"
                              onClick={() => openComplex(row.complexId)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>


      <EditComplexModal
        complex={editingComplex}
        isOpen={Boolean(editingComplex)}
        onClose={() => setEditingComplex(null)}
        onSaved={handleSaved}
      />
    </>
  );
}
